<template>
  <div class="flex-child">
    <div ref="chartContainer" class="vue-chart">
      <el-skeleton :loading="soldChartDataStatusInjected === AsyncDataStatus.PENDING" animated>
        <template #template>
          <el-skeleton-item style="height: 315px" />
        </template>
        <template #default>
          <apexchart
            v-if="soldChartDataStatusInjected === AsyncDataStatus.SUCCESS"
            width="100%"
            height="300"
            type="area"
            :options="chartOptions"
            :series="seriesList"
          />
        </template>
      </el-skeleton>
    </div>
    <div class="flex-text">
      <p v-html="$t('dashboardBoughtDaily', { label: formatters.label.call(currentTokenInfo?.name) })"></p>
      <p v-html="$t('dashboardBoughtTotal', { label: formatters.label.call(currentTokenInfo?.name) })"></p>
      <p
        v-if="currentToken === Tokens.pearl"
        v-html="$t('dashboardRetrievedTotal', { label: formatters.label.call(currentTokenInfo?.name) })"
      ></p>
      <p
        v-else
        v-html="$t('dashboardRetrievedOnIslandBvR', { label: formatters.label.call(currentTokenInfo?.name) })"
      ></p>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ChartDataFormatterProps } from '~/types/common';
import { Tokens } from '~/utils/constants';
import type { AsyncDataRequestStatus } from '#app';
import type { ChartDataModel, ChartLabelsModel, ChartSeriesModel, ITokenModel } from '~/types/apiService';
import { AsyncDataStatus } from '~/utils/constants/asyncData';
import { colors } from '~/utils/constants/charts';
import apexChartLocales from '~/utils/constants/apexChartLocales';
import type { ApexOptions } from 'apexcharts';

const store = useMainStore();
const { t, locale } = useI18n();
const chartId = 'dex-chart-sold-brought';
const chartContainer = ref<HTMLElement | null>(null);

const props = defineProps<ChartDataFormatterProps>();

const { data: currentTokenInfo } = useNuxtData<ITokenModel>('dashboardCurrentTokenInfo');
const { data: soldData } = useNuxtData<ChartDataModel>('statistics/sold');
const soldChartDataStatusInjected = inject<Ref<AsyncDataRequestStatus> | AsyncDataStatus>(
  'soldChartDataStatus',
  AsyncDataStatus.IDLE
);

const soldBroughtGraphSeries = computed(() => {
  const filteredSeries = soldData.value?.series.filter((serie: ChartSeriesModel) => {
    return !serie.name.includes('ROI') && !serie.name.includes('ReturnOfInvestment');
  });

  return {
    dates: soldData.value?.dates,
    series: filteredSeries
  } as ChartDataModel;
});

const currentToken = computed(() => store.currentToken);

const labels = computed<ChartLabelsModel>(() => ({
  type: 'datetime',
  categories: soldBroughtGraphSeries.value ? soldBroughtGraphSeries.value.dates : []
}));

const seriesList = computed(() => {
  if (!soldBroughtGraphSeries.value || !soldBroughtGraphSeries.value.series) return [];

  return soldBroughtGraphSeries.value.series.map((serie: ChartSeriesModel) => {
    return {
      ...serie,
      name: t(serie.name)
    };
  });
});

const chartOptions = computed(() => ({
  theme: {
    mode: 'dark'
  },
  grid: {
    borderColor: '#34414A'
  },
  chart: {
    id: chartId,
    class: 'vuechart-example',
    background: '#00000000',
    toolbar: {
      show: false
    },
    zoom: {
      enabled: false
    },
    locales: apexChartLocales,
    defaultLocale: locale.value
  },
  title: {
    text:
      store.currentToken === Tokens.pearl
        ? `${t('soldChatBoughtRetrieved')}`
        : `${t('soldChatBoughtRetrievedNonPearl')}`,
    align: 'center',
    style: {
      fontSize: '24px'
    }
  },
  stroke: {
    curve: 'smooth'
  },
  dataLabels: {
    enabled: false
  },
  yaxis: {
    forceNiceScale: true,
    decimalsInFloat: 0,
    labels: {
      formatter: function (val: number) {
        return props.formatters.tooltip.call(val);
      }
    }
  },
  ...colors,
  xaxis: labels.value,
  tooltip: {
    x: {
      format: 'dd/MM/yy'
    }
  }
}));

useApexChartUpdater({
  chartId,
  chartContainer,
  chartOptions: chartOptions as ComputedRef<ApexOptions>,
  seriesList,
  labels,
  extraDeps: [currentToken, locale]
});
</script>

<style lang="scss" scoped>
.vue-chart {
  min-height: 340px;
}
</style>
